<template>
    <modal
        name="product-enquiry-modal"
        width="50%"
        height="auto"
        scrollable
        @before-open="handleModalBeforeOpen"
    >
        <div class="py-8 px-10">

            <div class="flex justify-between">
                <div class="text-xl font-semibold tracking-wide">
                    Enquiry for {{ product.name }}
                </div>
                <CloseIcon
                    class="close-icon mt-1"
                    @click="closeModal"
                />
            </div>

            <div class="mt-4">
                <div class="grid md:grid-cols-2 gap-x-8 gap-y-6">
                    <Input
                        v-model="firstName"
                        label="First name"
                        type="text"
                        :invalid="$v.firstName.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.firstName.required && $v.firstName.$dirty">
                                Please enter your first name.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="lastName"
                        label="Last name"
                        type="text"
                        :invalid="$v.lastName.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.lastName.required && $v.lastName.$dirty">
                                Please enter your last name.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="company"
                        label="Company name"
                        type="text"
                        :invalid="$v.company.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.company.required && $v.firstName.$dirty">
                                Please enter your company name.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="email"
                        label="Email"
                        type="email"
                        :invalid="$v.email.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.email.required && $v.email.$dirty">
                                Please enter your email address.
                            </div>
                            <div v-if="!$v.email.email && $v.email.$dirty">
                                Please enter a valid email address.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="phone"
                        label="Phone"
                        type="tel"
                        :invalid="$v.phone.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.phone.required && $v.phone.$dirty">
                                Please enter your phone number.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="dates"
                        label="Date you need balloons"
                        type="text"
                        :invalid="$v.dates.$error"
                    >
                        <template #errors>
                            <div v-if="!$v.dates.required && $v.dates.$dirty">
                                Please enter your event date.
                            </div>
                        </template>
                    </Input>

                    <Input
                        v-model="deliveryAddress"
                        label="Delivery address"
                        type="text"
                    />

                    <div class="flex items-center">
                        <p class="text-sm">
                            To print your branding to a high standard we will require your
                            logo/brand message/slogan artwork in an EPS vector file.
                        </p>
                    </div>
                </div>

            </div>

            <div class="flex items-center mt-6">
                <Button
                    :loading="loading"
                    text="Send enquiry"
                    class="mr-8"
                    @click="send"
                >
                    <template #icon>
                        <EnvelopeIcon class="h-5 w-auto mr-2" />
                    </template>
                </Button>
                <span
                    class="cancel"
                    @click="closeModal"
                >
                    Cancel
                </span>
            </div>

        </div>
    </modal>
</template>

<script>
import EnvelopeIcon from 'static/fonts/LineIcons/envelope.svg';
import { email, required } from 'vuelidate/lib/validators';
import Swal from 'sweetalert2';
import CloseIcon from '~/static/fonts/LineIcons/close.svg';
import Button from '~/components/Button';
import Input from '@/components/Input.vue';
import handleException from '@/plugins/error-handler.client';

export default {

    components: {
        Input,
        EnvelopeIcon,
        CloseIcon,
        Button,
    },

    props: {
        product: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            firstName: '',
            lastName: '',
            company: '',
            email: '',
            phone: '',
            dates: '',
            deliveryAddress: '',
            loading: false,
        };
    },

    methods: {
        handleModalBeforeOpen() {
            if (this.$store.getters['user/isLoggedIn']) {
                this.firstName = this.$store.getters['user/firstName'];
                this.lastName = this.$store.getters['user/lastName'];
                this.email = this.$store.getters['user/email'];
                this.phone = this.$store.getters['user/phone'];
            }
        },
        async send() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            this.loading = true;
            try {
                await this.$axios.$post('/product-enquiry', {
                    productId: this.product.id,
                    firstName: this.firstName,
                    lastName: this.lastName,
                    company: this.company,
                    email: this.email,
                    phone: this.phone,
                    dates: this.dates,
                    deliveryAddress: this.deliveryAddress,
                });

                await Swal.fire({
                    icon: 'success',
                    title: 'Enquiry sent',
                    text: 'Our team will get back to you shortly.',
                });

                this.$modal.hide('product-enquiry-modal');
            } catch (e) {
                handleException(e);
            } finally {
                this.loading = false;
            }
        },
        closeModal() {
            this.$modal.hide('product-enquiry-modal');
        },
    },

    validations: {
        firstName: {
            required,
        },
        lastName: {
            required,
        },
        company: {
            required,
        },
        email: {
            required,
            email,
        },
        phone: {
            required,
        },
        dates: {
            required,
        },
    },

};
</script>

<style lang="scss" scoped>
.close-icon {
    @apply h-5 cursor-pointer transition-fast;
    &:hover {
        @apply opacity-50;
    }
}
.cancel {
    @apply font-semibold text-grey transition-fast cursor-pointer;
    &:hover {
        @apply text-primary;
    }
}
</style>
